import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Suspense, useState } from 'react'
import { companyQueryOptions } from '@/entities/company'
import {
  type Project,
  projectQueryOptions,
  useArchiveProject,
  useUpdateProject,
} from '@/entities/project'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { MoveProjectDialog } from '@/features/manage-project'
import { useSearchStore } from '@/features/search'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { InlineText } from '@/shared/ui/inline-edit'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { VisibilityMatrix } from '@/widgets/visibility-matrix'

export const Route = createFileRoute('/_app/projects/$projectId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(projectQueryOptions(params.projectId)),
  component: ProjectPage,
})

function ProjectPage() {
  const { projectId } = Route.useParams()
  const project = useQuery(projectQueryOptions(projectId))

  if (project.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (project.isError || !project.data) {
    return (
      <StatusScreen title="Проект не найден" message="Ссылка устарела или проект удалён.">
        <Link to="/companies">
          <Button variant="secondary">К компаниям</Button>
        </Link>
      </StatusScreen>
    )
  }
  return <ProjectBody project={project.data} />
}

function ProjectBody({ project }: { project: Project }) {
  const { isAdmin } = useSession()
  const openWithTag = useSearchStore((s) => s.openWithTag)
  const company = useQuery(companyQueryOptions(project.company_id))
  const update = useUpdateProject(project.id)
  const archive = useArchiveProject(project.id)
  const [moveOpen, setMoveOpen] = useState(false)

  const save = (overrides: { name?: string; slug?: string; description?: string }) =>
    update.mutateAsync({ ...overrides, version: project.version })

  const saveName = (v: string) => {
    if (!v.trim()) return Promise.reject(new Error('empty'))
    return save({ name: v.trim() })
  }

  return (
    <>
      <header className="mb-6 flex flex-col">
        {project.archived && (
          <div className="mb-2">
            <Badge tone="warning">В архиве</Badge>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <h1 className="t-h2 min-w-0 flex-1 basis-56 text-text-primary">
            <InlineText
              value={project.name}
              ariaLabel="Название проекта"
              canEdit={isAdmin}
              placeholder="Название"
              emptyText="Без названия"
              onSave={saveName}
            />
          </h1>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => setMoveOpen(true)}>
                Перенести
              </Button>
              <Button
                variant={project.archived ? 'secondary' : 'danger'}
                onClick={() => archive.mutate(!project.archived)}
                disabled={archive.isPending}
              >
                {project.archived ? 'Вернуть из архива' : 'В архив'}
              </Button>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="flex items-center gap-2">
            <span className="text-text-secondary">Компания:</span>
            <Link
              to="/companies/$companyId"
              params={{ companyId: project.company_id }}
              className="text-accent hover:underline"
            >
              {company.data?.name ?? '…'}
            </Link>
          </span>
          <span className="flex items-center gap-2">
            <span className="text-text-secondary">Slug:</span>
            <span className="font-mono text-xs text-text-secondary">
              <InlineText
                value={project.slug}
                ariaLabel="Slug проекта"
                canEdit={isAdmin}
                placeholder="slug"
                emptyText="—"
                onSave={(v) => save({ slug: v.trim() })}
              />
            </span>
          </span>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Описание">
          <div className="text-sm">
            <InlineText
              value={project.description}
              ariaLabel="Описание проекта"
              canEdit={isAdmin}
              multiline
              placeholder="Опишите проект…"
              emptyText="Описания нет"
              onSave={(v) => save({ description: v.trim() })}
            />
          </div>
        </Card>

        <Card title="Видимость в сервисах">
          <Suspense fallback={<Spinner />}>
            <VisibilityMatrix kind="project" id={project.id} canEdit={isAdmin} />
          </Suspense>
        </Card>
      </div>

      <Card title="Теги" className="mt-6">
        <EntityTagsEditor entityKind="project" entityId={project.id} onTagClick={openWithTag} />
      </Card>

      <MoveProjectDialog project={project} open={moveOpen} onClose={() => setMoveOpen(false)} />
    </>
  )
}
